import React, { useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Button,
  Link,
  VStack,
  HStack,
  Progress,
  IconButton,
  Spinner,
  Icon,
  Collapse,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, CheckIcon, CloseIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { getPasswordStrength } from '../utils';

interface UsernamePasswordFormProps {
  username: string;
  password: string;
  confirmPassword: string;
  isSignup: boolean;
  error: string;
  loading: boolean;
  usernameAvailable: boolean | null;
  checkingUsername: boolean;
  discreteAlternativeAuth?: boolean; // If true, show alternatives in collapsible "Having trouble?" section
  // Theme colors
  formLabelColor: string;
  formFieldBg: string;
  formFieldBorder: string;
  formFieldBorderFocus: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accentBlue: string;
  accentHover: string;
  // Handlers
  onUsernameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onToggleMode: () => void;
  onSubmit: () => void;
  onForgotPassword: () => void;
  onSwitchToPhone?: () => void;
  onSwitchToEmail?: () => void;
}

/**
 * Username/Password authentication form
 *
 * Features:
 * - Toggle between Login/Signup modes
 * - Real-time username availability check (signup only)
 * - Password visibility toggle
 * - Password strength indicator (signup only)
 * - Confirm password validation (signup only)
 * - Forgot password link
 * - Links to switch to phone/email authentication
 */
export const UsernamePasswordForm: React.FC<UsernamePasswordFormProps> = ({
  username,
  password,
  confirmPassword,
  isSignup,
  error,
  loading,
  usernameAvailable,
  checkingUsername,
  discreteAlternativeAuth = false,
  formLabelColor,
  formFieldBg,
  formFieldBorder,
  formFieldBorderFocus,
  textPrimary,
  textSecondary,
  textMuted,
  accentBlue,
  accentHover,
  onUsernameChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onToggleMode,
  onSubmit,
  onForgotPassword,
  onSwitchToPhone,
  onSwitchToEmail,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showAlternativeOptions, setShowAlternativeOptions] = useState(false);

  // Get password strength for signup mode
  const passwordStrength = getPasswordStrength(password);

  // Username availability indicator
  const renderUsernameStatus = () => {
    if (!isSignup || username.length < 3) return null;

    if (checkingUsername) {
      return <Spinner size="xs" color={accentBlue} />;
    }

    if (usernameAvailable === true) {
      return <Icon as={CheckIcon} color="green.500" />;
    }

    if (usernameAvailable === false) {
      return <Icon as={CloseIcon} color="red.500" />;
    }

    return null;
  };

  // Get username helper text
  const getUsernameHelperText = () => {
    if (!isSignup) return null;

    if (checkingUsername) {
      return 'Checking availability...';
    }

    if (usernameAvailable === true) {
      return 'Username is available';
    }

    if (usernameAvailable === false) {
      return 'Username is already taken';
    }

    if (username.length > 0 && username.length < 3) {
      return 'Username must be at least 3 characters';
    }

    return 'Letters, numbers, and underscores only';
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      onSubmit();
    }
  };

  return (
    <Box>
      <VStack spacing={4} align="stretch">
        {/* Username Field */}
        <FormControl isInvalid={isSignup && usernameAvailable === false}>
          <FormLabel color={formLabelColor}>Username</FormLabel>
          <InputGroup>
            <Input
              type="text"
              placeholder="your_username"
              value={username}
              onChange={(e) => onUsernameChange(e.target.value.toLowerCase())}
              onKeyPress={handleKeyPress}
              autoFocus
              autoComplete="username"
              bg={formFieldBg}
              color={textPrimary}
              borderColor={formFieldBorder}
              _placeholder={{ color: textMuted }}
              _focus={{ borderColor: formFieldBorderFocus }}
              _hover={{ borderColor: formFieldBorderFocus }}
            />
            <InputRightElement>
              {renderUsernameStatus()}
            </InputRightElement>
          </InputGroup>
          {isSignup && (
            <FormHelperText
              color={
                usernameAvailable === true
                  ? 'green.500'
                  : usernameAvailable === false
                  ? 'red.500'
                  : textMuted
              }
            >
              {getUsernameHelperText()}
            </FormHelperText>
          )}
        </FormControl>

        {/* Password Field */}
        <FormControl isInvalid={!!error && error.toLowerCase().includes('password')}>
          <FormLabel color={formLabelColor}>Password</FormLabel>
          <InputGroup>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              onKeyPress={handleKeyPress}
              autoComplete={isSignup ? 'new-password' : 'current-password'}
              bg={formFieldBg}
              color={textPrimary}
              borderColor={formFieldBorder}
              _placeholder={{ color: textMuted }}
              _focus={{ borderColor: formFieldBorderFocus }}
              _hover={{ borderColor: formFieldBorderFocus }}
            />
            <InputRightElement>
              <IconButton
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                variant="ghost"
                size="sm"
                onClick={() => setShowPassword(!showPassword)}
                color={textMuted}
                _hover={{ color: textPrimary }}
              />
            </InputRightElement>
          </InputGroup>

          {/* Password strength indicator (signup only) */}
          {isSignup && password.length > 0 && (
            <Box mt={2}>
              <Progress
                value={(passwordStrength.score / 4) * 100}
                size="xs"
                colorScheme={
                  passwordStrength.score <= 1
                    ? 'red'
                    : passwordStrength.score === 2
                    ? 'yellow'
                    : 'green'
                }
                borderRadius="full"
              />
              <Text fontSize="xs" color={passwordStrength.color} mt={1}>
                Password strength: {passwordStrength.label}
              </Text>
            </Box>
          )}

          {/* Forgot password link (login only) */}
          {!isSignup && (
            <HStack justify="flex-end" mt={1}>
              <Link
                fontSize="sm"
                color={accentBlue}
                onClick={onForgotPassword}
                _hover={{ textDecoration: 'underline' }}
              >
                Forgot password?
              </Link>
            </HStack>
          )}
        </FormControl>

        {/* Confirm Password Field (signup only) */}
        {isSignup && (
          <FormControl
            isInvalid={confirmPassword.length > 0 && password !== confirmPassword}
          >
            <FormLabel color={formLabelColor}>Confirm Password</FormLabel>
            <InputGroup>
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => onConfirmPasswordChange(e.target.value)}
                onKeyPress={handleKeyPress}
                autoComplete="new-password"
                bg={formFieldBg}
                color={textPrimary}
                borderColor={formFieldBorder}
                _placeholder={{ color: textMuted }}
                _focus={{ borderColor: formFieldBorderFocus }}
                _hover={{ borderColor: formFieldBorderFocus }}
              />
              <InputRightElement>
                <IconButton
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  color={textMuted}
                  _hover={{ color: textPrimary }}
                />
              </InputRightElement>
            </InputGroup>
            {confirmPassword.length > 0 && password !== confirmPassword && (
              <FormErrorMessage>Passwords do not match</FormErrorMessage>
            )}
          </FormControl>
        )}

        {/* Error message */}
        {error && (
          <Text color="red.500" fontSize="sm" textAlign="center">
            {error}
          </Text>
        )}

        {/* Submit button */}
        <Button
          bg={accentBlue}
          color="white"
          width="100%"
          mt={2}
          onClick={onSubmit}
          isLoading={loading}
          isDisabled={isSignup && usernameAvailable === false}
          _hover={{ bg: accentHover }}
        >
          {isSignup ? 'Create Account' : 'Sign In'}
        </Button>

        {/* Toggle between login/signup */}
        <Text textAlign="center" fontSize="sm" color={textSecondary}>
          {isSignup ? (
            <>
              Already have an account?{' '}
              <Link color={accentBlue} onClick={onToggleMode}>
                Sign in
              </Link>
            </>
          ) : (
            <>
              Don't have an account?{' '}
              <Link color={accentBlue} onClick={onToggleMode}>
                Create one
              </Link>
            </>
          )}
        </Text>

        {/* Alternative login methods - only show if at least one alternative is available */}
        {(onSwitchToPhone || onSwitchToEmail) && (
          discreteAlternativeAuth ? (
            /* Discrete mode: show collapsible "Having trouble?" section */
            <Box mt={4}>
              <Link
                fontSize="sm"
                color={textMuted}
                onClick={() => setShowAlternativeOptions(!showAlternativeOptions)}
                _hover={{ color: textSecondary }}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                Having trouble signing in?
                <Icon
                  as={showAlternativeOptions ? ChevronUpIcon : ChevronDownIcon}
                  ml={1}
                  boxSize={4}
                />
              </Link>
              <Collapse in={showAlternativeOptions} animateOpacity>
                <VStack spacing={2} mt={3} p={3} bg={formFieldBg} borderRadius="md">
                  <Text fontSize="xs" color={textMuted} textAlign="center">
                    Try an alternative sign-in method:
                  </Text>
                  {onSwitchToPhone && (
                    <Link
                      fontSize="sm"
                      color={accentBlue}
                      onClick={onSwitchToPhone}
                      _hover={{ textDecoration: 'underline' }}
                    >
                      Sign in with phone number
                    </Link>
                  )}
                  {onSwitchToEmail && (
                    <Link
                      fontSize="sm"
                      color={accentBlue}
                      onClick={onSwitchToEmail}
                      _hover={{ textDecoration: 'underline' }}
                    >
                      Sign in with email
                    </Link>
                  )}
                </VStack>
              </Collapse>
            </Box>
          ) : (
            /* Standard mode: show prominent OR divider with links */
            <>
              <HStack spacing={4} my={2}>
                <Box flex={1} h="1px" bg={formFieldBorder} />
                <Text fontSize="xs" color={textMuted}>
                  OR
                </Text>
                <Box flex={1} h="1px" bg={formFieldBorder} />
              </HStack>

              <VStack spacing={2}>
                {onSwitchToPhone && (
                  <Link
                    fontSize="sm"
                    color={accentBlue}
                    onClick={onSwitchToPhone}
                    _hover={{ textDecoration: 'underline' }}
                  >
                    Sign in with phone number
                  </Link>
                )}
                {onSwitchToEmail && (
                  <Link
                    fontSize="sm"
                    color={accentBlue}
                    onClick={onSwitchToEmail}
                    _hover={{ textDecoration: 'underline' }}
                  >
                    Sign in with email
                  </Link>
                )}
              </VStack>
            </>
          )
        )}
      </VStack>
    </Box>
  );
};
